import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom"
import * as Highcharts from "highcharts";
import { SeriesOptionsType } from "highcharts";
import { ExpensesData } from "@baseTypes/service/dashboard/IExpensesDashboardService";
import LinearChart from "../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import PlanFactTooltip from "@modules/dashboards/dashboardTooltips/TooltipVariants/PlanFactTooltip/PlanFactTooltip";
import { CircleLabel } from "../../works/detalization/dynamics";
import { applyTip, groupByProp_, shortMonthNames, wideFontSize20 } from "@utils/utils";
import { positionerWithOffset } from "@utils/highchart/tooltip";
import st from './styles.module.scss';
import PlanFactLegend from "@components/elements/planFactLegend/PlanFactLegend";
import { Align, Rotate, Toggler } from "@baseTypes/baseType";
import Legend from "@elements/Legend/Legend";
import FlexLine from "@elements/flexLine/FlexLine";

interface ExpensesByArticlesProps {
    listOfExpensesForExpensesDash: ExpensesData[]
}

export const onHighlightSeries: Highcharts.PointMouseOverCallbackFunction = function (e) {
    const points = this.series.chart.series.flatMap((i) => i?.points)
    points.forEach((p) => {
        // @ts-ignore
        p?.graphic?.css({ opacity: .2 })
    })
    const groupedPoints = groupByProp_<Highcharts.Point>('index')(points)
    groupedPoints[this.index]?.forEach((p) => {
        // @ts-ignore
        p?.graphic?.css({ opacity: 1 })
    })
}

export const onCancelHighlightSeries: Highcharts.PointMouseOverCallbackFunction = function (e) {
    const points = this.series.chart.series.flatMap((i) => i?.points);
    points.forEach((p: any) => {
        p?.graphic?.css({ opacity: 1 })
    })
}
const ExpensesByArticles: FC<ExpensesByArticlesProps> = (props) => {

    let {
        listOfExpensesForExpensesDash
    } = props

    let navigate = useNavigate()

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({ title: { text: '' } })
    console.log('listOfExpensesForExpensesDash', listOfExpensesForExpensesDash)
    const plan = useMemo(() => {
        return listOfExpensesForExpensesDash.length > 0 ? listOfExpensesForExpensesDash
            .map(i => i.simple.byMonth.map(c => c[0]))
            .reduce((acc, curr) => acc.map((v, i) => curr[i] + v))
            .map(y => ({ y, className: st.column })) : []
    }, [listOfExpensesForExpensesDash])

    const planStack: SeriesOptionsType[] = useMemo(() => {
        return listOfExpensesForExpensesDash.map((i: ExpensesData, idx) => {
            return {
                name: i.title,
                color: i.color2,
                pointWidth: 10,
                stack: 'П',
                id: 'stack_plan_' + idx,
                pointStart: 0.25,
                type: 'column',
                data: i.simple.byMonth.map((j) => ({ y: j[0], className: st.column }))
            } satisfies SeriesOptionsType
        })
    }, [listOfExpensesForExpensesDash])
    const factStack: SeriesOptionsType[] = useMemo(() => {
        return listOfExpensesForExpensesDash.map((i: ExpensesData, idx) => {
            return {
                name: i.title,
                id: 'stack_fact_' + idx,
                color: i.color2,
                pointWidth: 10,
                stack: 'Ф',
                showInLegend: false,
                pointStart: -0.25,
                type: 'column',
                data: i.simple.byMonth.map((j) => ({ y: j[1], className: st.column }))
            } satisfies SeriesOptionsType
        })
    }, [listOfExpensesForExpensesDash])

    const fact = useMemo(() => {
        return listOfExpensesForExpensesDash.length > 0 ? listOfExpensesForExpensesDash
            .map(i => i.simple.byMonth.map(c => c[1]))
            .reduce((acc, curr) => acc.map((v, i) => curr[i] + v))
            .map(y => ({ y, className: st.column })) : []
    }, [listOfExpensesForExpensesDash])
    useEffect(() => {
        console.log({
            fact, plan, factStack, planStack
        })
    }, [plan, fact, planStack, factStack]);
    useEffect(() => {
        const defaultOptions: Highcharts.Options = {
            title: {
                text: ''
            },
            credits: {
                enabled: false
            },
            legend: {
                enabled: false
            },
            tooltip: applyTip((ctx, tooltip) => PlanFactTooltip({
                expenses: listOfExpensesForExpensesDash,
                monthIndex: tooltip.chart.hoverPoint?.index,
                type: 'simple',
            }), {
                shared: false,
                positioner: positionerWithOffset({ x: 10 })
            }),
            yAxis: {
                type: "linear",
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false
                },
                labels: {
                    style: {
                        fontSize: wideFontSize20()
                    }
                },
            },
            accessibility: {
                announceNewData: {
                    enabled: true
                }
            },
            xAxis: {
                categories: shortMonthNames,
                labels: {
                    style: {
                        fontSize: wideFontSize20(),
                        color: 'rgba(85, 87, 89, 1)',
                        fontWeight: '500'
                    }
                },
                lineColor: 'rgba(205, 206, 209, 1)',
                tickColor: 'rgba(205, 206, 209, 1)'
            },

            plotOptions: {
                series: {

                    opacity: 1,
                    pointInterval: 1,
                    allowPointSelect: true,
                    marker: {
                        enabled: false
                    }
                },
                column: {
                    point: {
                        events: {
                            mouseOver: onHighlightSeries,
                            mouseOut: onCancelHighlightSeries
                        }
                    },
                    states: {
                        inactive: {
                            opacity: 1
                        },
                        hover: {
                            enabled: false
                        }
                    },
                    stacking: 'normal',
                    groupPadding: 0.5,
                    pointPadding: 0.1,
                    pointWidth: 20,
                    dataLabels: {
                        enabled: true,
                        style: { color: 'black', fontWeight: '100' },
                        inside: false,
                        format: ''
                    },
                    events: {
                        click: function (e) {
                            const month = e.point.index + 1
                            navigate(`expenses/${month}`)
                        }
                    }
                }
            },
            series: [
                ({
                    "name": "План",
                    "pointWidth": 25,
                    stacking: undefined,
                    pointStart: 0.25,
                    "type": 'column',
                    id: 'plan',
                    "color": "rgba(134, 206, 82, 0.4)",
                    "data": plan
                }),
                ({
                    "name": "Факт",
                    id: 'fact',
                    "pointWidth": 25,
                    stacking: undefined,
                    pointStart: -0.25,
                    "type": 'column',
                    "color": "rgba(254, 124, 102, 0.4)",
                    "data": fact
                }),
                ...planStack,
                ...factStack
            ]
        }
        const onLegendClick = function (event: any) {
            // @ts-ignore
            let myname = this.name;
            // @ts-ignore
            let myvis = !this.visible;
            // @ts-ignore
            this.chart.series.forEach(function (elem) {
                if (elem.name === myname) {
                    elem.setVisible(myvis);
                }
            });
            return false;
        };


        // @ts-ignore
        defaultOptions.series.forEach(function (serie) {
            serie.events = { legendItemClick: onLegendClick };
        });
        setChartOptions(defaultOptions)
    }, [listOfExpensesForExpensesDash])

    const additionalRows = useMemo(() =>
        listOfExpensesForExpensesDash.map((expenseItem, idx) =>
            ({
                id: ['stack_plan_' + idx, 'stack_fact_' + idx],
                color: expenseItem.color2,
                values: [<CircleLabel color={expenseItem.color2} name={expenseItem.title}/>]
            })
        ), [listOfExpensesForExpensesDash])

    const [chartApi, setChartApi] = useState<Highcharts.Chart>()

    return (
        <>
            <LinearChart options={chartOptions} callback={setChartApi}/>
            <div className={st['legend']}>
                <FlexLine align={Align.Start} className={st['legend__articles']}>
                    <Legend rotate={Rotate.Row}
                            chartApi={chartApi}
                            toggler={Toggler.series}
                            rows={additionalRows}
                    />
                </FlexLine>
                <PlanFactLegend chartApi={chartApi}
                                align={Align.End}
                                padding={{ right: 10 }}
                />
            </div>
        </>
    )
}

export default ExpensesByArticles;